import { CalendarGrid } from './CalendarGrid';
import { JourneyHelp } from '../../../components/ui/JourneyHelp';
import { eventLocalDay, eventRange } from './event-time';
import { useState, useMemo } from 'react';
import { useEvents, useCreateEvent } from '../../../platform/api/hooks';
import { asList, asRecord, str } from '../../../platform/api/utils';
import { PageHeader } from '../../../components/ui/PageHeader';
import { Badge } from '../../../components/ui/Badge';
import { Section } from '../../../components/ui/Section';

import {
  MONTHS,
  STATUS_LABELS,
  STATUS_VARIANTS,
  TYPE_COLORS,
  TYPE_LABELS,
  formatTime,
} from './calendar-page.support';
import { CalendarCreateModal, CalendarDetailModal } from './calendar-page.modals';
import { onlyIf, usePermissions } from '../../../platform/auth/permissions';

export function CalendarPage() {
  const { can } = usePermissions();
  const today = new Date();
  const [viewDate, setViewDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState<Record<string, unknown> | null>(null);
  const [visibleInMemberApp, setVisibleInMemberApp] = useState(false);
  const [requirePresence, setRequirePresence] = useState(false);

  const { data: rawEvents, isLoading, isError, refetch } = useEvents();
  const createEvent = useCreateEvent();

  const events = asList(rawEvents);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startOffset = firstDay.getDay();
  const daysInMonth = lastDay.getDate();

  function changeMonth(delta: number) {
    setViewDate(new Date(year, month + delta, 1));
    setSelectedDay(null);
  }

  const dayEvents = useMemo(() => {
    const map: Record<number, Record<string, unknown>[]> = {};
    for (const e of events) {
      const r = asRecord(e);
      const dateStr = eventLocalDay(r.startsAt);
      if (!dateStr) continue;
      const d = parseInt(dateStr.substring(8, 10), 10);
      if (isNaN(d)) continue;
      const eventDate = new Date(`${dateStr}T12:00:00`);
      if (eventDate.getFullYear() !== year || eventDate.getMonth() !== month) continue;
      map[d] ??= [];
      map[d].push(r);
    }
    return map;
  }, [events, year, month]);

  const selectedEvents = selectedDay ? (dayEvents[selectedDay] ?? []) : [];

  const [newTitle, setNewTitle] = useState('');
  const [newDate, setNewDate] = useState('');
  const [newType, setNewType] = useState('MEETING');
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('12:00');
  const [createError, setCreateError] = useState('');
  const [notice, setNotice] = useState('');

  async function handleCreate() {
    if (!can('calendar.events.create') || createEvent.isPending) return;
    setCreateError('');
    try {
      const range = eventRange(newDate, startTime, endTime);
      const created = asRecord(
        await createEvent.mutateAsync({
          title: newTitle.trim(),
          type: newType,
          startsAt: range.startsAt,
          endsAt: range.endsAt,
          visibleInMemberApp,
          requirePresenceConfirmation: requirePresence,
        }),
      );
      if (!str(created.id)) {
        setCreateError(
          'A resposta não confirmou o cadastro. Consulte o calendário antes de tentar novamente.',
        );
        return;
      }
      setViewDate(new Date(range.date.getFullYear(), range.date.getMonth(), 1));
      setSelectedDay(range.date.getDate());
      setShowCreate(false);
      setNewTitle('');
      setNewDate('');
      setVisibleInMemberApp(false);
      setRequirePresence(false);
      setNotice('Atividade salva. Consulte os detalhes no dia selecionado.');
      await refetch();
    } catch {
      setCreateError(
        'Não foi possível salvar. Seus dados continuam aqui. Confira os campos e seu acesso antes de tentar novamente.',
      );
    }
  }

  return (
    <div style={{ padding: '2.25rem 2.5rem', maxWidth: 1100 }}>
      <PageHeader
        title="Calendário"
        subtitle={`${MONTHS[month] ?? String(month)} de ${String(year)}`}
        action={onlyIf(can('calendar.events.create'), {
          label: '+ Novo evento',
          onClick: () => {
            setShowCreate(true);
          },
        })}
      />

      <JourneyHelp title="Como organizar uma atividade?">
        <p>
          Selecione um dia para consultar as atividades. Abra uma atividade para ver seus detalhes.
        </p>
        {can('calendar.events.create') && (
          <p>
            Use Novo evento e informe título, data e horários. O horário segue este dispositivo. A
            atividade só será confirmada após o servidor aceitar a gravação. Confira o dia
            selecionado depois de salvar.
          </p>
        )}
        <p>
          Fechar o formulário mantém o rascunho enquanto esta tela estiver aberta. Sair da página
          descarta o rascunho. A orientação pode ser fechada e reaberta.
        </p>
      </JourneyHelp>
      {notice && <p role="status">{notice}</p>}
      {isError && (
        <p role="alert">
          Não foi possível consultar o calendário.{' '}
          <button
            type="button"
            onClick={() => {
              void refetch();
            }}
          >
            Tentar novamente
          </button>
        </p>
      )}
      {!isError && (
        <div
          className="journey-calendar"
          style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}
        >
          <CalendarGrid
            year={year}
            month={month}
            daysInMonth={daysInMonth}
            startOffset={startOffset}
            dayEvents={dayEvents}
            isLoading={isLoading}
            today={today}
            selectedDay={selectedDay}
            onChangeMonth={changeMonth}
            onSelectDay={setSelectedDay}
          />

          {/* Side panel: selected day events */}
          <div style={{ width: 320, flexShrink: 0 }}>
            {selectedDay ? (
              <Section title={`Dia ${String(selectedDay)}`}>
                {selectedEvents.length === 0 ? (
                  <p style={{ color: 'var(--faint)', fontSize: '0.85rem' }}>
                    Nenhum evento neste dia.
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {selectedEvents.map((ev) => (
                      <button
                        type="button"
                        key={str(ev.id)}
                        onClick={() => {
                          setShowDetail(ev);
                        }}
                        style={{
                          padding: '0.6rem 0.75rem',
                          borderRadius: 10,
                          background: TYPE_COLORS[str(ev.type)] ?? 'var(--paper)',
                          border: '1px solid var(--line-soft)',
                          cursor: 'pointer',
                          textAlign: 'left',
                          width: '100%',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '0.25rem',
                          }}
                        >
                          <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                            {str(ev.title)}
                          </span>
                          <Badge
                            label={STATUS_LABELS[str(ev.status)] ?? str(ev.status)}
                            variant={STATUS_VARIANTS[str(ev.status)] ?? 'default'}
                          />
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>
                          {formatTime(ev.startsAt)} - {formatTime(ev.endsAt)} &middot;{' '}
                          {TYPE_LABELS[str(ev.type)] ?? '—'}
                        </div>
                        {str(ev.location) && (
                          <div style={{ fontSize: '0.7rem', color: 'var(--faint)' }}>
                            {str(ev.location)}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </Section>
            ) : (
              <div
                style={{
                  padding: '2rem 1rem',
                  textAlign: 'center',
                  color: 'var(--faint)',
                  fontSize: '0.85rem',
                  background: '#fff',
                  borderRadius: 14,
                  border: '1px solid var(--line)',
                }}
              >
                Selecione um dia para ver os eventos
              </div>
            )}
          </div>
        </div>
      )}

      {showCreate && can('calendar.events.create') && (
        <CalendarCreateModal
          newTitle={newTitle}
          setNewTitle={setNewTitle}
          newDate={newDate}
          setNewDate={setNewDate}
          newType={newType}
          setNewType={setNewType}
          visibleInMemberApp={visibleInMemberApp}
          setVisibleInMemberApp={setVisibleInMemberApp}
          requirePresence={requirePresence}
          setRequirePresence={setRequirePresence}
          startTime={startTime}
          setStartTime={setStartTime}
          endTime={endTime}
          setEndTime={setEndTime}
          error={createError}
          createPending={createEvent.isPending}
          onCancel={() => {
            setShowCreate(false);
          }}
          onCreate={() => {
            void handleCreate();
          }}
        />
      )}
      {showDetail && (
        <CalendarDetailModal
          event={showDetail}
          onClose={() => {
            setShowDetail(null);
          }}
        />
      )}
    </div>
  );
}
